import api from '../../../../utils/api';
import apiNode from '../../apiNode';
import { riasecQuestions } from '../../../assessments/riasecQuestions';
import { ensureSession, fetchProgress, saveProgress } from '../sessionApi';
import { ConnectionError } from '../answerOutbox';
import { getAssessmentReleaseStatus } from '../../assessmentRelease';
import { guidanceOfficeFact, inTurn } from './common';

// What a Yes points to, said back the way a person listening would ("So you like…"); still no
// praise, so a Yes isn't encouraged over a No
const REFLECT = {
  realistic: { en: ['So you like working with your hands.', 'Fixing and building, okay.'], fil: ['Ah, gusto mo pala ang gawaing gamit ang kamay.', 'Pag-aayos at paggawa, okay.'] },
  investigative: { en: ['So you like figuring things out.', 'Working out how things work, okay.'], fil: ['Ah, gusto mo palang inaalam kung paano gumagana ang mga bagay.', 'Pag-iisip at pagsusuri, okay.'] },
  artistic: { en: ['So you like making things your own way.', 'Creative stuff, okay.'], fil: ['Ah, gusto mo palang gumawa sa sarili mong paraan.', 'Malikhaing gawain, okay.'] },
  social: { en: ['So you like being with people.', 'Working with others, okay.'], fil: ['Ah, gusto mo palang kasama ang mga tao.', 'Pakikisama sa iba, okay.'] },
  enterprising: { en: ['So you like taking the lead.', 'Being in charge, okay.'], fil: ['Ah, gusto mo palang namumuno.', 'Pagiging lider, okay.'] },
  conventional: { en: ['So you like things in order.', 'Plans and order, okay.'], fil: ['Ah, gusto mo palang maayos ang lahat.', 'Plano at kaayusan, okay.'] },
};
// A No is just as fine as a Yes
const NOT_FOR_YOU = {
  en: ["Okay, not really you. That's fine.", 'Fair enough. Next one.', 'Not your thing? No problem.'],
  fil: ['Okay, hindi masyado. Ayos lang.', 'Sige, next tayo.', 'Hindi mo hilig? Walang problema.'],
};
import { matchAnswer } from '../../riasec/answerMatcher';
import {
  scoreAnswers,
  topThree,
  riasecCodeOf,
  recommendationByType,
  strandKey,
  courseStrands,
} from '../../riasec/riasecLogic';

const TOTAL = riasecQuestions.length;

const titleCase = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());

const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * Every course in the catalog (the chat ranks them itself) for the saved result, [] when the
 * course service can't be reached. With no connection the test waits and finishes once the
 * phone is back online, so the result keeps its program list; one more try covers a blip, and
 * a course service that stays out of reach still lets the test finish (as before).
 */
const coursesForResult = async () => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await apiNode.get('/api/courses');
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      if (error?.response) return [];
      if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new ConnectionError('Courses not reached');
      if (attempt === 0) await pause(2000);
    }
  }
  return [];
};

const answersFromResponses = (responses) =>
  riasecQuestions.map((q) => {
    const value = String(responses?.[q.id] ?? responses?.[String(q.id)] ?? '').toLowerCase();
    return value === 'yes' || value === 'no' ? value : undefined;
  });

const riasec = {
  key: 'riasec',
  total: TOTAL,
  feedbackCategory: 'chatbot',
  followupReason: 'riasec_followup',
  counselorFallbackReason: 'career',
  resultsTab: 'riasec',
  names: {
    title: { en: 'Career interests', fil: 'Career interests' },
    short: { en: 'Career interests', fil: 'Career interests' },
    code: 'RIASEC',
  },
  greeting: {
    en: "Let's start the RIASEC test! This will help determine your career path. 😊",
    fil: 'Simulan natin ang RIASEC test! Ito ay tutulong sa pagpili ng inyong career path. 😊',
  },
  // The steps between the last answer and the result; TestChat shows them instead of typing dots
  working: {
    lead: { en: "That's all 42! Give me a moment.", fil: 'Iyan na ang 42! Sandali lang.' },
    title: { en: 'Working out your result…', fil: 'Inaayos ang resulta mo…' },
    steps: [
      { key: 'counted', text: { en: 'Counted your 42 answers', fil: 'Nabilang na ang 42 sagot mo' } },
      { key: 'programs', text: { en: 'Finding programs that fit you', fil: 'Hinahanap ang mga programang bagay sa iyo' } },
      { key: 'saving', text: { en: 'Saving your result', fil: 'Sine-save ang resulta mo' } },
    ],
    slow: { en: 'Still working, the program list is big.', fil: 'Tumatagal pa, malaki ang listahan ng mga programa.' },
  },
  facts: (lang, school) => [
    { icon: 'list', text: lang === 'fil' ? `${TOTAL} mabilis na tanong na oo o hindi` : `${TOTAL} quick yes-or-no questions` },
    { icon: 'graduation-cap', text: lang === 'fil' ? 'Sa dulo: mga programang bagay sa iyo at sa iyong strand' : 'At the end: programs that fit you and your strand' },
    guidanceOfficeFact(lang, school),
  ],
  introNote: {
    en: 'No wrong answers. Tap a button or type your answer.',
    fil: 'Walang maling sagot. Pindutin ang button o i-type ang iyong sagot.',
  },
  layout: 'yesno',
  scale: [
    { value: 'yes', label: { en: 'Yes', fil: 'Oo' } },
    { value: 'no', label: { en: 'No', fil: 'Hindi' } },
  ],
  prompt: { en: 'Yes or no?', fil: 'Oo o hindi?' },
  questionText: (i) => riasecQuestions[i].text,
  // The questions are in English only, so Anna reads them with the English voice style
  speechLanguage: () => 'en',
  // Everyday situations for each question, open by default on a computer like the other tests
  examples: (i, lang) => {
    const q = riasecQuestions[i];
    return (q && (lang === 'fil' ? q.examplesFil : q.examples)) || [];
  },
  echo: (value, lang) => (value === 'yes' ? (lang === 'fil' ? 'Oo' : 'Yes') : lang === 'fil' ? 'Hindi' : 'No'),
  // Check-ins after 15, 21 and 32 answers (one emoji each, rewritten 2026-10-20)
  checkpoints: {
    15: { en: "15 down! Let's keep going. 🙌", fil: '15 na! Tuloy lang tayo. 🙌' },
    21: { en: 'Halfway there! 21 more to go. 😊', fil: 'Kalahati na! 21 pa. 😊' },
    32: { en: 'Just 10 left. Almost done! 🎉', fil: '10 na lang. Malapit na! 🎉' },
  },
  typing: {
    placeholder: { en: 'Or type your answer', fil: 'O i-type ang iyong sagot' },
    reask: { en: "Hmm, I didn't catch that. Would you say yes or no?", fil: 'Hmm, hindi ko nakuha. Oo ba o hindi?' },
    match: (text) => matchAnswer(text),
    countedTag: (value, lang) =>
      value === 'yes' ? (lang === 'fil' ? 'Binilang na Oo' : 'Counted as Yes') : lang === 'fil' ? 'Binilang na Hindi' : 'Counted as No',
  },
  isWorrying: () => false,
  reply: (i, value, lang, n) => {
    if (value === 'yes') {
      const lines = REFLECT[riasecQuestions[i].type];
      return lines ? { gap: 3, text: inTurn(lines, lang, n) } : null;
    }
    return { gap: 6, text: inTurn(NOT_FOR_YOU, lang, n) };
  },

  load: async () => {
    const progress = await fetchProgress('riasec');
    if (!progress || progress.status === 'completed' || !progress.session_id) {
      return { sessionId: null, nextIndex: 0, answers: [] };
    }
    const answers = answersFromResponses(progress.responses);
    // What the student typed for each answer, so later saves don't replace it with "Yes"/"No"
    const replies = progress.session_data?.raw_replies || {};
    const raws = riasecQuestions.map((q) => replies[String(q.id)]);
    const answered = answers.filter(Boolean).length;
    const current = Math.min(Number(progress.current) || 0, TOTAL);
    // Every question answered but no result saved yet: finish now
    if (current >= TOTAL && answered >= TOTAL) return { sessionId: progress.session_id, nextIndex: TOTAL, answers, raws };
    if (current > 0 && current < TOTAL) return { sessionId: progress.session_id, nextIndex: current, answers, raws };
    return { sessionId: progress.session_id, nextIndex: 0, answers: [] };
  },

  save: async ({ sessionId, nextIndex, answers, raws = [] }) => {
    const id = await ensureSession('riasec', TOTAL, sessionId);
    const responses = {};
    const rawReplies = {};
    riasecQuestions.forEach((q, i) => {
      if (answers[i] === 'yes' || answers[i] === 'no') {
        responses[q.id] = answers[i];
        rawReplies[q.id] = raws[i] || (answers[i] === 'yes' ? 'Yes' : 'No');
      }
    });
    const last = riasecQuestions[nextIndex - 1];
    await saveProgress({
      session_id: id,
      assessment_type: 'riasec',
      current_step: nextIndex,
      scores: scoreAnswers(answers),
      responses,
      raw_replies: rawReplies,
      question_number: nextIndex,
      question_text: last ? last.text : undefined,
      next_question_text: riasecQuestions[nextIndex] ? riasecQuestions[nextIndex].text : undefined,
    });
    return id;
  },

  // Save the result as soon as it's worked out, with the same record shape as before
  complete: async ({ sessionId, answers, context = {}, onProgress }) => {
    const id = await ensureSession('riasec', TOTAL, sessionId);
    const scores = scoreAnswers(answers);
    const top = topThree(scores);
    if (onProgress) onProgress('programs');
    const courses = await coursesForResult();
    if (onProgress) onProgress('saving');
    const user = context.user || {};
    const strand = strandKey(user.strand);
    const response = await api.post('/api/save-conversation/', {
      session_id: id,
      name: user.name ? titleCase(user.name) : 'Student',
      sex: user.sex || 'N/A',
      strand: user.strand || 'N/A',
      riasec_code: riasecCodeOf(scores),
      riasec_course_recommendation: recommendationByType(courses, top),
      strand_course_recommendation: strand ? courses.filter((c) => courseStrands(c).includes(strand)).map((c) => c.name) : [],
      realistic_score: scores.realistic,
      investigative_score: scores.investigative,
      artistic_score: scores.artistic,
      social_score: scores.social,
      enterprising_score: scores.enterprising,
      conventional_score: scores.conventional,
    }, { skipErrorToast: true, timeout: 45000 });
    if (response.status !== 201 && !response.data?.already_saved) throw new Error('Not saved');
    return { sessionId: id, data: { scores, top, courses } };
  },

  releaseStatus: (sessionId) => getAssessmentReleaseStatus('riasec', sessionId),
};

export default riasec;
